import { useQuery } from '@tanstack/react-query';
import StationsApi from '../lib/api/StationsApi';

/**
 * Paged, city-filtered stations list backing the Stations table.
 *
 * Unlike `useCities`, this query key is deliberately NOT a singleton: stations
 * are server-paginated, so each page/city combination is its own cache entry.
 * A shared key would serve page 1's rows for every page.
 *
 * No `staleTime: Infinity` either — cities never change during a session, but
 * stations are edited in this very UI (and a later task adds mutations that
 * invalidate this key), so this leaves `staleTime` at the default.
 *
 * `keepPreviousData` reproduces the Redux version's behaviour of keeping the
 * previous page's rows on screen while the next page loads, instead of
 * blanking the grid.
 *
 * `count` (rows in this page) and `total` (true total across all pages) are
 * surfaced separately and never derived from one another. `total` is `null`
 * on the search branch and is passed through untouched — defaulting it to 0
 * would collapse the pager to a single page.
 *
 * `sortBy`/`sortDir` are IN THE QUERY KEY, for exactly the same reason `city`
 * and `offset` are: the sort is applied server-side, so two sorts are two
 * different result sets. A sort-blind key would hand the previous sort's rows
 * back from cache and the grid would show stale rows under a freshly-flipped
 * header arrow — silently, since `keepPreviousData` means nothing even blinks.
 *
 * `sortDir` is dropped when `sortBy` is absent, in the key and in the request
 * alike. The backend ignores a direction with no column (`buildOrderBy` returns
 * undefined without `sortBy`), so keeping it would let the key vary on a
 * parameter that cannot change the response — two cache entries for one result
 * set, and a refetch on a no-op change.
 */
const useStations = ({ city, page = 1, pageSize = 25, sortBy, sortDir } = {}) => {
  const offset = (page - 1) * pageSize;

  const sort = sortBy ? { sortBy, sortDir } : { sortBy: undefined, sortDir: undefined };

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      'stations',
      {
        city: city ?? null,
        offset,
        limit: pageSize,
        sortBy: sort.sortBy ?? null,
        sortDir: sort.sortDir ?? null,
      },
    ],
    queryFn: () => StationsApi.getStations({ city, offset, limit: pageSize, ...sort }),
    keepPreviousData: true,
  });

  return {
    stations: data?.data ?? [],
    count: data?.count ?? 0,
    total: data?.total ?? null,
    isLoading,
    isFetching,
    error,
  };
};

export default useStations;
